import { type MouseEvent } from "react";

/**
 * SkipLink - Skip links for keyboard navigation
 *
 * Provides visible skip links that appear when focused via keyboard navigation.
 * These links allow users to skip to main content, navigation, or footer.
 *
 * Features:
 * - Visible on focus (keyboard navigation)
 * - Hidden by default (screen reader accessible)
 * - Smooth scroll to target
 * - Proper ARIA labels
 * - Keyboard shortcuts: Alt+M (main), Alt+N (nav), Alt+F (footer)
 */
export default function SkipLink() {
  const handleSkipTo = (targetId: string, selector?: string) => {
    return (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const target = selector
        ? document.querySelector(selector)
        : document.getElementById(targetId) || document.querySelector(`#${targetId}`);

      if (target instanceof HTMLElement) {
        target.setAttribute("tabindex", "-1");
        target.focus();

        // Respect reduced motion preference
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        target.scrollIntoView({
          behavior: prefersReducedMotion ? "auto" : "smooth",
          block: "start",
        });

        // Remove tabindex after focus event completes
        const removeTabIndex = () => {
          if (document.activeElement === target) {
            target.removeAttribute("tabindex");
          }
        };

        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
          requestAnimationFrame(removeTabIndex);
        });
      }
    };
  };

  return (
    <nav
      aria-label="Skip navigation links"
      className="sr-only focus-within:not-sr-only focus-within:absolute focus-within:top-4 focus-within:left-4 focus-within:z-[200] focus-within:flex focus-within:flex-col focus-within:gap-2"
    >
      <a
        href="#main-content"
        className="px-4 py-2 bg-white text-[#09091e] rounded-md font-medium shadow-lg outline-none ring-2 ring-white ring-offset-2 ring-offset-[#09091e] focus:outline-none hover:opacity-90 transition-opacity"
        onClick={handleSkipTo("main-content", "main, [role='main']")}
      >
        Skip to main content
      </a>
      <a
        href="#main-navigation"
        className="px-4 py-2 bg-white text-[#09091e] rounded-md font-medium shadow-lg outline-none ring-2 ring-white ring-offset-2 ring-offset-[#09091e] focus:outline-none hover:opacity-90 transition-opacity"
        onClick={handleSkipTo("main-navigation", "#main-navigation, nav")}
      >
        Skip to navigation
      </a>
      <a
        href="#footer"
        className="px-4 py-2 bg-white text-[#09091e] rounded-md font-medium shadow-lg outline-none ring-2 ring-white ring-offset-2 ring-offset-[#09091e] focus:outline-none hover:opacity-90 transition-opacity"
        onClick={handleSkipTo("footer", "#footer, footer")}
      >
        Skip to footer
      </a>
    </nav>
  );
}
